import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/owner';
import type Owner from '@ember/owner';
import type Notifications from './notifications';
import type Session from './session';
import { apiFetch } from '../fetch';
import { isNativePlatform } from '../utils/native-platform';

const PUSH_TOKEN_API_PATH = '/api/me/push-tokens';

/**
 * Store the registered push token to avoid re-registering on every app restart.
 * Keyed by user ID so a different account on the same device always re-registers.
 */
function getRegisteredTokenKey(userId: string): string {
  return `favoris:push-token:${userId}`;
}

/**
 * Store whether we've asked the user for push permission.
 * Keyed by user ID so we only ask once per user.
 */
function getPermissionAskedKey(userId: string): string {
  return `favoris:push-permission-asked:${userId}`;
}

/**
 * Push Notifications Service
 *
 * Manages FCM push notification registration and permission requests.
 *
 * Handles:
 * - Registering FCM listeners (called once at app startup)
 * - Requesting push notification permission
 * - Tracking per-user permission ask status
 * - Registering push tokens with the backend API
 *
 * @example
 * ```typescript
 * import { service } from '@ember/service';
 *
 * @service declare pushNotifications: PushNotifications;
 *
 * // Request permission on first login (if not already asked)
 * await this.pushNotifications.requestPermissionIfNeeded(userId);
 *
 * // Request permission on manual submission (every time)
 * await this.pushNotifications.requestPermission(userId);
 * ```
 */
export default class PushNotifications extends Service {
  /** Track if listeners are already registered to avoid duplicate registration */
  private listenersRegistered = false;

  /**
   * Source ID from a tapped notification, set before the router is ready.
   * Consumed by the application route to navigate after session/auth setup.
   */
  @tracked pendingSourceId: string | null = null;

  /**
   * Resolves once registerListeners() has completed.
   * Awaited by the application route so navigation decisions are made
   * after the pushNotificationActionPerformed listener is in place.
   */
  private _notifyReady!: (value: void) => void;
  readonly readyPromise: Promise<void> = new Promise<void>((resolve) => {
    this._notifyReady = resolve;
  });

  /** Called by the instance-initializer once registerListeners() is done. */
  notifyReady(): void {
    this._notifyReady();
  }

  constructor(owner: Owner) {
    super(owner);
  }

  /**
   * Register FCM listeners for token registration and notification display.
   * Should be called once at app startup.
   * On web (non-native), registers the service worker instead.
   */
  async registerListeners(): Promise<void> {
    if (this.listenersRegistered) {
      console.log('[push] registerListeners: already registered, skipping');
      return;
    }

    const isNative = await isNativePlatform();
    console.log('[push] registerListeners: isNativePlatform =', isNative);
    if (!isNative) {
      await this.registerServiceWorker();
      this.listenersRegistered = true;
      return;
    }

    try {
      const { PushNotifications } =
        await import('@capacitor/push-notifications');

      console.log('[push] Adding FCM listeners (registration + received)');

      // Register listeners BEFORE calling register() to avoid missing the
      // registration event if it fires synchronously on some platforms.
      await PushNotifications.addListener('registration', (tokenData) => {
        console.log(
          '[push] FCM registration token received:',
          tokenData.value.slice(-8),
          '(last 8 chars)'
        );
        void this.handleTokenRegistration(tokenData.value);
      });

      await PushNotifications.addListener('registrationError', (error) => {
        console.error('[push] FCM registration error:', JSON.stringify(error));
      });

      // Handle foreground push notification display
      await PushNotifications.addListener(
        'pushNotificationReceived',
        (notification) => {
          console.log(
            '[push] Push notification received in foreground:',
            notification.title,
            '/',
            notification.body
          );
          const notifications: unknown = getOwner(this)?.lookup(
            'service:notifications'
          );
          if (
            notifications &&
            typeof notifications === 'object' &&
            'info' in notifications &&
            (notification.title ?? notification.body)
          ) {
            const typedNotifications = notifications as Notifications;
            typedNotifications.info(
              notification.body ?? notification.title ?? '',
              {
                description:
                  notification.body && notification.title
                    ? notification.title
                    : undefined,
                duration: 6000,
              }
            );
          }
        }
      );

      await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action) => {
          console.log(
            '[push] Push notification action performed:',
            action.actionId,
            action.notification?.data
          );
          const data = action.notification?.data as
            Record<string, string> | undefined;
          const contentSourceId = data?.contentSourceId;
          if (!contentSourceId) return;

          // Store for the application route to consume on cold start
          // (when the router is not yet ready for transitionTo).
          this.pendingSourceId = contentSourceId;

          // Also try direct navigation for background -> foreground case
          // where the router is already initialised.
          const router = getOwner(this)?.lookup('service:router') as
            | {
                currentRouteName?: string;
                transitionTo: (path: string) => void;
              }
            | undefined;
          if (router?.currentRouteName) {
            router.transitionTo(`/sources/${contentSourceId}`);
            this.pendingSourceId = null;
          }
        }
      );

      this.listenersRegistered = true;
      console.log('[push] All FCM listeners registered');
    } catch (err) {
      console.error('[push] registerListeners failed:', err);
    }
  }

  /**
   * Request push notification permission and register for push notifications.
   * - Native (Android/iOS): requests FCM permission via Capacitor
   * - Web: subscribes to Web Push API via service worker + VAPID
   *
   * @param userId - The user ID to associate the push token with
   */
  async requestPermission(userId: string): Promise<void> {
    const isNative = await isNativePlatform();
    console.log('[push] requestPermission: isNativePlatform =', isNative);
    if (!isNative) {
      await this.requestWebPushPermission(userId);
      return;
    }

    try {
      const { PushNotifications } =
        await import('@capacitor/push-notifications');

      console.log('[push] Requesting push notification permissions...');
      // Request permission
      const permStatus = await PushNotifications.requestPermissions();
      console.log('[push] Permission status:', permStatus.receive);
      if (permStatus.receive !== 'granted') {
        console.warn('[push] Permission not granted:', permStatus.receive);
        return;
      }

      // Ensure listeners are registered before calling register()
      await this.registerListeners();

      console.log('[push] Calling PushNotifications.register()...');
      // Register with FCM/APNS — listener is already set up above
      await PushNotifications.register();
      console.log(
        '[push] PushNotifications.register() called — waiting for token event'
      );
    } catch (err) {
      console.error('[push] requestPermission failed:', err);
    }
  }

  /**
   * Request push notification permission only if we haven't asked this user before.
   *
   * @param userId - The user ID to check permission ask status for
   */
  async requestPermissionIfNeeded(userId: string): Promise<void> {
    const alreadyAsked = this.hasAskedPermission(userId);
    console.log(
      '[push] requestPermissionIfNeeded: userId =',
      userId,
      '| alreadyAsked =',
      alreadyAsked
    );
    if (alreadyAsked) {
      const tokenKey = getRegisteredTokenKey(userId);
      const storedToken = localStorage.getItem(tokenKey);
      if (!storedToken) {
        // Permission was asked before but token never saved — re-request FCM registration
        console.log(
          '[push] Permission was asked before but no token stored — re-registering'
        );
        await this.requestPermission(userId);
      } else {
        // Token is stored locally — re-sync with API to recover from stale localStorage
        // (e.g. DB was cleared, user switched devices, or this is a fresh app install
        // that picked up an old emulator's localStorage). The API does an upsert so
        // this call is idempotent and cheap.
        console.log(
          '[push] Re-syncing stored token with API to ensure DB is up to date'
        );
        await this.handleTokenRegistration(storedToken, true);
      }
      return;
    }

    await this.requestPermission(userId);
    this.markPermissionAsked(userId);
  }

  /**
   * Check if we've already asked this user for push permission.
   *
   * @param userId - The user ID to check
   */
  hasAskedPermission(userId: string): boolean {
    const key = getPermissionAskedKey(userId);
    return localStorage.getItem(key) === 'true';
  }

  /**
   * Mark that we've asked this user for push permission.
   *
   * @param userId - The user ID to mark
   */
  markPermissionAsked(userId: string): void {
    const key = getPermissionAskedKey(userId);
    localStorage.setItem(key, 'true');
  }

  /**
   * Handle FCM token registration with the backend API.
   * Called when a new token is received from FCM, or when doing a forced re-sync.
   *
   * @param token - The FCM token to register
   * @param force - Skip the "already registered" check to force an API upsert (used for re-sync)
   */
  private async handleTokenRegistration(
    token: string,
    force = false
  ): Promise<void> {
    console.log(
      '[push] handleTokenRegistration called, token tail:',
      token.slice(-8)
    );
    const session: unknown = getOwner(this)?.lookup('service:session');
    const typedSession = session as Session | undefined;
    if (!typedSession?.isAuthenticated) {
      console.warn(
        '[push] Not authenticated — storing token as pending for later'
      );
      // Not authenticated — store raw token locally and retry when the
      // session changes (handled by the session observer below).
      localStorage.setItem('favoris:push-token:pending', token);
      return;
    }

    const authToken = typedSession.data.authenticated?.accessToken;
    const userId = typedSession.user?.id;
    if (!userId) {
      console.warn(
        '[push] Authenticated but no userId yet — storing as pending'
      );
      // Authenticated but user ID not yet available — store as pending and
      // rely on the next app start to register.
      localStorage.setItem('favoris:push-token:pending', token);
      return;
    }
    const tokenKey = getRegisteredTokenKey(userId);
    const lastToken = localStorage.getItem(tokenKey);
    if (!force && lastToken === token) {
      console.log(
        '[push] Token already registered for user',
        userId,
        '— skipping API call'
      );
      return;
    }

    console.log('[push] Registering token with API for user', userId, '...');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    try {
      const response = await apiFetch(PUSH_TOKEN_API_PATH, {
        method: 'POST',
        headers,
        body: JSON.stringify({ token, platform: 'fcm' }),
      });

      if (response.ok) {
        console.log(
          '[push] Token registered successfully with API for user',
          userId
        );
        localStorage.setItem(tokenKey, token);
        localStorage.removeItem('favoris:push-token:pending');
      } else {
        console.error(
          '[push] API rejected token registration:',
          response.status,
          response.statusText
        );
      }
    } catch (err) {
      console.error('[push] Network error registering token:', err);
    }
  }

  // ─── Web Push (browser) ────────────────────────────────────────────────────

  /**
   * Register the service worker for web push support.
   * No-op if service workers are not supported or already registered.
   */
  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.log('[push] Service workers not supported in this browser');
      return;
    }
    try {
      await navigator.serviceWorker.register('/sw.js');
      console.log('[push] Service worker registered');
    } catch (err) {
      console.error('[push] Service worker registration failed:', err);
    }
  }

  /**
   * Request web push permission and subscribe via the PushManager API.
   * Fetches the VAPID public key from the API, then creates a push subscription
   * and registers it with the backend.
   *
   * @param userId - The user ID to associate the subscription with
   */
  private async requestWebPushPermission(userId: string): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('[push] Web Push not supported in this browser');
      return;
    }

    try {
      // Fetch the VAPID public key from the API
      const keyResponse = await apiFetch(
        '/api/push-notifications/vapid-public-key'
      );
      if (!keyResponse.ok) {
        console.warn(
          '[push] VAPID public key not available (status',
          keyResponse.status,
          ')'
        );
        return;
      }
      const { data } = (await keyResponse.json()) as {
        data: { publicKey: string };
      };
      const vapidPublicKey = data.publicKey;

      // Get the service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Request notification permission
      const permission = await Notification.requestPermission();
      console.log('[push] Web push permission:', permission);
      if (permission !== 'granted') {
        console.warn('[push] Web push permission not granted');
        return;
      }

      // Subscribe via PushManager
      // applicationServerKey accepts a base64url string directly (RFC 8292)
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      });

      console.log('[push] Web push subscription created');

      // Register subscription with API
      const subscriptionJson = JSON.stringify(subscription);
      await this.handleWebPushTokenRegistration(subscriptionJson, userId);
    } catch (err) {
      console.error('[push] requestWebPushPermission failed:', err);
    }
  }

  // ─── Status + unsubscribe (settings toggle) ────────────────────────────────

  /**
   * Current push notification status for the settings toggle.
   * - 'unsupported': browser lacks service worker / PushManager support (web only)
   * - 'blocked': permission was explicitly denied — can only be undone in
   *   browser/device settings, not re-prompted from JS
   * - 'on': a token/subscription is currently registered
   * - 'off': permission available but not currently subscribed
   *
   * @param userId - The user ID to check registration status for
   */
  async getPushStatus(
    userId: string
  ): Promise<'unsupported' | 'blocked' | 'on' | 'off'> {
    const isNative = await isNativePlatform();

    if (isNative) {
      try {
        const { PushNotifications } =
          await import('@capacitor/push-notifications');
        const status = await PushNotifications.checkPermissions();
        if (status.receive === 'denied') return 'blocked';
      } catch (err) {
        console.error('[push] checkPermissions failed:', err);
      }
      const storedToken = localStorage.getItem(getRegisteredTokenKey(userId));
      return storedToken ? 'on' : 'off';
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return 'unsupported';
    }
    if (Notification.permission === 'denied') return 'blocked';

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return subscription ? 'on' : 'off';
    } catch (err) {
      console.error('[push] getSubscription failed:', err);
      return 'off';
    }
  }

  /**
   * Turn off push notifications for this device.
   * - Web: unsubscribes the browser PushSubscription (actually stops delivery)
   *   and removes the token from the backend.
   * - Native: cannot revoke OS-level permission from JS — only removes the
   *   token from the backend so the worker stops targeting this device. The
   *   user must disable notifications for the app in Android settings to
   *   fully revoke.
   *
   * @param userId - The authenticated user's ID
   */
  async unsubscribe(userId: string): Promise<void> {
    const session: unknown = getOwner(this)?.lookup('service:session');
    const typedSession = session as Session | undefined;
    const authToken = typedSession?.data.authenticated?.accessToken;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const tokenKey = getRegisteredTokenKey(userId);

    try {
      const isNative = await isNativePlatform();

      if (isNative) {
        const storedToken = localStorage.getItem(tokenKey);
        if (storedToken) {
          await apiFetch(PUSH_TOKEN_API_PATH, {
            method: 'DELETE',
            headers,
            body: JSON.stringify({ token: storedToken }),
          });
          localStorage.removeItem(tokenKey);
        }
        return;
      }

      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      const subscriptionJson = JSON.stringify(subscription);
      await subscription.unsubscribe();
      await apiFetch(PUSH_TOKEN_API_PATH, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ token: subscriptionJson }),
      });
      localStorage.removeItem(tokenKey);
    } catch (err) {
      console.error('[push] unsubscribe failed:', err);
    }
  }

  /**
   * Open the device's notification settings screen for this app (Android
   * only). Used when permission has been denied — the browser/OS won't let
   * JS re-prompt, so the user has to flip it on manually.
   */
  async openNativeSettings(): Promise<void> {
    try {
      const { NativeSettings, AndroidSettings } =
        await import('capacitor-native-settings');
      await NativeSettings.openAndroid({
        option: AndroidSettings.AppNotification,
      });
    } catch (err) {
      console.error('[push] openNativeSettings failed:', err);
    }
  }

  /**
   * Register a web push subscription JSON string with the backend API.
   *
   * @param subscriptionJson - JSON-serialised PushSubscription object
   * @param userId - The authenticated user's ID
   */
  private async handleWebPushTokenRegistration(
    subscriptionJson: string,
    userId: string
  ): Promise<void> {
    const session: unknown = getOwner(this)?.lookup('service:session');
    const typedSession = session as Session | undefined;
    const authToken = typedSession?.data.authenticated?.accessToken;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

    try {
      const response = await apiFetch(PUSH_TOKEN_API_PATH, {
        method: 'POST',
        headers,
        body: JSON.stringify({ token: subscriptionJson, platform: 'webpush' }),
      });

      if (response.ok) {
        console.log(
          '[push] Web push subscription registered with API for user',
          userId
        );
        localStorage.setItem(getRegisteredTokenKey(userId), subscriptionJson);
        localStorage.removeItem('favoris:push-token:pending');
      } else {
        console.error(
          '[push] API rejected web push subscription:',
          response.status
        );
      }
    } catch (err) {
      console.error(
        '[push] Network error registering web push subscription:',
        err
      );
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'push-notifications': PushNotifications;
  }
}
